import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Close, Email, WorkOutline } from "@mui/icons-material";
import Searchbar from "components/Searchbar";
import fetchJobs from "layouts/pages/jobs/actions/fetchJobs";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import fetchSingleApplicant from "layouts/pages/applicants/actions/fetchSingleApplicant";
import { useSnackbar } from "notistack";
import { useAppContextController } from "context/AppContext";
import SendMessage from "components/SendMessage";
import CircularLoader from "components/CircularLoader";
import { ApplicantsTable } from "layouts/pages/events/components/BulkMessagingModal/ApplicantsTable";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

const BulkMessageDynamicFormModal = ({ open, onClose, formName, formId, formShortName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { companyType } = useAppContextController();
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [subject, setSubject] = useState(`Form: ${formName}`);
  const [message, setMessage] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [allRows, setRows] = useState([]);
  const [text, setText] = useState(false);
  const [email, setEmail] = useState(true);
  const [system, setSystem] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [pushNotificationSelection, setPushNotificationSelection] = useState(null);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Load applicants when a job is selected
  useEffect(() => {
    const fetchJobApplicants = async () => {
      if (!selectedJob) return;

      try {
        setLoadingApplicants(true);
        const options = {
          fetchAll: true,
          filters: {
            "jobs.jobSlug": selectedJob.jobSlug,
          },
          useElemMatch: true,
        };

        const response = await fetchApplicants(options);
        if (response?.data) {
          setApplicants(response.data);
        } else {
          setApplicants([]);
        }
      } catch (error) {
        console.error("Error fetching applicants:", error);
        enqueueSnackbar("Failed to load applicants", { variant: "error" });
      } finally {
        setLoadingApplicants(false);
      }
    };

    fetchJobApplicants();
  }, [selectedJob, enqueueSnackbar]);

  // Handle selecting a job from search
  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setTabValue(1); // Switch to message tab

    // Pre-populate the message with form information
    setMessage(
      `We would like to share the ${formName} form with you. Please complete it at your earliest convenience.`
    );
  };

  // Get recipient for validity check - similar to BulkMessagingModal
  const getRecipientForValidity = (propVal) => {
    let tempId;
    selectedRecipients.forEach((selRec) => {
      const row = allRows.find((app) => !app[propVal] && app._id === selRec);
      if (row) tempId = row._id;
    });
    return tempId;
  };

  // Set recipient similar to BulkMessagingModal
  useEffect(() => {
    const getFirstRecipient = async (id) => {
      try {
        const rec = await fetchSingleApplicant({ applicantId: id });
        if (rec?.data) setRecipient(rec.data);
      } catch (error) {
        console.error("Error fetching recipient:", error);
      }
    };

    if (selectedRecipients?.length) {
      const rec = selectedRecipients[0];
      let emailId;
      let phoneId;
      if (email === false) emailId = getRecipientForValidity("email");
      if (text === false) phoneId = getRecipientForValidity("phone");

      if ((!emailId && phoneId) || (emailId && !phoneId)) {
        const id = emailId || phoneId;
        getFirstRecipient(id);
      }
      if (emailId && phoneId) setRecipient({ id: emailId });
      if (!emailId && !phoneId) {
        getFirstRecipient(rec);
      }
    } else {
      setRecipient(null);
    }
  }, [selectedRecipients, text, email, allRows]);

  // Disable button check
  const disableButton = React.useMemo(() => {
    if (pushNotificationSelection === "selected-individuals") {
      return !selectedRecipients.length || !recipient;
    }
    return (!selectedRecipients.length || !recipient) && !pushNotificationSelection;
  }, [selectedRecipients, recipient, pushNotificationSelection]);

  // Handle sending the form
  const handleSend = async () => {
    if (!selectedJob || selectedRecipients.length === 0) {
      enqueueSnackbar("Please select a job and at least one applicant", { variant: "warning" });
      return;
    }

    try {
      setIsLoading(true);

      // This would be replaced with an actual API call to send forms
      console.log("Sending form to job:", selectedJob);
      console.log("To applicants:", selectedRecipients);
      console.log("Form ID:", formId);
      console.log("Subject:", subject);
      console.log("Message:", message);

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      enqueueSnackbar(`Form sent to ${selectedRecipients.length} applicants successfully`, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      console.error("Error sending form:", error);
      enqueueSnackbar("Failed to send form to applicants", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Columns for search results display
  const columns = [
    { title: "Job URL", field: "jobSlug" },
    { title: "Title", field: "title" },
    { title: "Venue", field: "venueName" },
    { title: "Status", field: "status" },
  ];

  // Create recipients table component
  const recipientsTable = (
    <ApplicantsTable
      applicants={applicants}
      setRows={setRows}
      selectedRecipients={selectedRecipients}
      setSelectedRecipients={setSelectedRecipients}
      emailChecked={email}
      smsChecked={text}
      systemChecked={system}
      isLoading={loadingApplicants}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Form Distribution: {formName}</Typography>
          <Button onClick={onClose} color="inherit" size="small" sx={{ minWidth: "auto" }}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <Tabs value={tabValue} onChange={handleTabChange} centered>
        <Tab label="Select Job" icon={<WorkOutline />} iconPosition="start" />
        <Tab label="Message" icon={<Email />} iconPosition="start" disabled={!selectedJob} />
      </Tabs>

      <DialogContent>
        <TabPanel value={tabValue} index={0}>
          <Box mb={3} sx={{ minHeight: "60vh" }}>
            <Typography variant="subtitle1" gutterBottom>
              Search for a job to send this form to:
            </Typography>
            <Searchbar
              fetch={fetchJobs}
              fetchAll={false}
              placeholder="Search Job/Venue"
              columns={columns}
              queryCharacterLimit={2}
              resultsLimit={10}
              setFilters={setFilters}
              setPage={setPage}
              searchBy={["title", "venueName", "venueSlug"]}
              filterBy="venueSlug"
              onRowClick={(job) => handleSelectJob(job)}
            />
          </Box>

          {selectedJob && (
            <Paper
              sx={{
                p: 2,
                mt: 2,
                border: "1px solid #4caf50",
                borderRadius: 2,
                bgcolor: "rgba(76, 175, 80, 0.08)",
              }}
              elevation={1}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Selected Job:
                  </Typography>
                  <Typography variant="body1">{selectedJob.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedJob.venueName} • {selectedJob.jobSlug}
                  </Typography>
                </Box>
                <Button variant="contained" color="primary" onClick={() => setTabValue(1)}>
                  Continue
                </Button>
              </Box>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {selectedJob && (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Sending form for: <strong>{selectedJob.title}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Venue: {selectedJob.venueName} | Job ID: {selectedJob.jobSlug}
                </Typography>
              </Box>

              {isLoading ? (
                <Box sx={{ height: "100px", display: "relative" }}>
                  <CircularLoader isLoading color="info" hasBackdrop={false} />
                </Box>
              ) : (
                <SendMessage
                  recipient={
                    recipient || { _id: "", email: "", phone: "", firstName: "", lastName: "" }
                  }
                  companyType={companyType}
                  setOpen={onClose}
                  text={text}
                  setText={setText}
                  email={email}
                  setEmail={setEmail}
                  system={system}
                  setSystem={setSystem}
                  isButtonDisabled={disableButton}
                  bulk
                  selectedRecipients={selectedRecipients}
                  messageContext="Applicant"
                  attachmentContext="Form"
                  pushNotificationSelection={pushNotificationSelection}
                  setPushNotificationSelection={setPushNotificationSelection}
                  recipientsTable={recipientsTable}
                  templateIn="Form for the Required Information"
                  templateSelectionDisabled
                  noText
                  isPanel
                  jobId={selectedJob._id}
                  applicants={applicants}
                  customFormInfo={{
                    formId,
                    formName,
                    shortName: formShortName || formName,
                  }}
                />
              )}
            </>
          )}
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

BulkMessageDynamicFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  formShortName: PropTypes.string,
};

BulkMessageDynamicFormModal.defaultProps = {
  formShortName: "",
};

export default BulkMessageDynamicFormModal;
